import { BaseStore } from "@omni/types/enums/baseStore.enum";
import { getBaseStoreName } from "@omni/utils/baseStore.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { capitalize } from "lodash-es";

export type SortableFacet = {
    label: string;
    refined: boolean;
    count?: number;
};
export type SortFunction = (a: SortableFacet, b: SortableFacet) => number;

type SortDirection = "asc" | "desc";
type SortByFactory = (direction: SortDirection) => SortFunction;

const applyDirection = (direction: SortDirection, sortResult: number) =>
    (direction === "asc" ? 1 : -1) * sortResult;

const sortByNumericLabel: SortFunction = (a, b) => {
    const aFloat = parseFloat(a.label);
    const bFloat = parseFloat(b.label);

    return Number.isNaN(aFloat) || Number.isNaN(bFloat)
        ? capitalize(a.label).localeCompare(capitalize(b.label))
        : aFloat - bFloat;
};

const createSortByIsRefined: SortByFactory = (direction) => (a, b) =>
    applyDirection(direction, a.refined === b.refined ? 0 : a.refined ? 1 : -1);

const createSortByLabel: SortByFactory = (direction) => (a, b) =>
    applyDirection(direction, sortByNumericLabel(a, b));

const createSortByCount: SortByFactory = (direction) => (a, b) =>
    applyDirection(
        direction,
        isDefined(a.count) && isDefined(b.count) ? a.count - b.count : 0
    );

const createSortByBrandHubo: SortByFactory = (direction) => (a, b) => {
    const HUBO_BASE_STORE = getBaseStoreName(BaseStore.Hubo);

    return applyDirection(
        direction,
        a.label === HUBO_BASE_STORE ? 1 : b.label === HUBO_BASE_STORE ? -1 : 0
    );
};

export const RefinementSort = {
    NAME_ASC: createSortByLabel("asc"),
    NAME_DESC: createSortByLabel("desc"),
    COUNT_ASC: createSortByCount("asc"),
    COUNT_DESC: createSortByCount("desc"),
    IS_REFINED_ASC: createSortByIsRefined("asc"),
    IS_REFINED_DESC: createSortByIsRefined("desc"),
    BRAND_HUBO: createSortByBrandHubo("desc")
};

export const createSortBy: (sortFunctions: SortFunction[]) => SortFunction =
    (sortFunctions) => (a, b) => {
        for (const sortFunction of sortFunctions) {
            const sortResult = sortFunction(a, b);
            if (sortResult !== 0) return sortResult;
        }

        return 0;
    };
